<template>
  <div v-if="hasSlides"
       class="timeline">
    <state-manager :value="currentState">
      <div class="timeline__header">
        <button class="timeline__toggle timeline__button"
                type="button"
                @click="toggleAutoPlay">
          <template v-if="autoPlay">
            <font-awesome-icon icon="pause-circle"
                               aria-hidden="true"></font-awesome-icon>
            Pause
          </template>
          <template v-else>
            <font-awesome-icon icon="play-circle"
                               aria-hidden="true"></font-awesome-icon>
            Autoplay
          </template>
        </button>

        <button
          v-show="showScrollButtons"
          ref="scrollLeft"
          v-click-hold="scrollYearsLeft"
          class="timeline__scroll timeline__button"
          type="button"
          title="Scroll left">
          <font-awesome-icon icon="chevron-left"
                             aria-hidden="true"></font-awesome-icon>
        </button>

        <nav
          v-resize="yearsResize"
          class="timeline__nav nav">
          <ol ref="years"
              class="nav__list">
            <li
              v-for="(slide, index) in slides"
              :id="'year-' + index"
              :key="index"
              class="nav__item"
              :class="{ nav__active: currentStateIndex === index }">
              <button
                class="nav__button timeline__button"
                :class="{ 'nav__button--active': currentStateIndex == index }"
                @click="currentStateIndex = index">
                {{ slide.year }}
              </button>
            </li>
          </ol>
        </nav>

        <button
          v-show="showScrollButtons"
          ref="scrollRight"
          v-click-hold="scrollYearsRight"
          class="timeline__scroll timeline__button"
          title="Scroll right"
          type="button">
          <font-awesome-icon icon="chevron-right"
                             aria-hidden="true"></font-awesome-icon>
        </button>

        <div class="timeline__prev-next">
          <button class="nav__button timeline__button"
                  type="button"
                  @click="decrementSlideIndex">
            Previous
          </button>

          <button class="nav__button timeline__button"
                  type="button"
                  @click="incrementSlideIndex">
            Next
          </button>
        </div>
      </div>

      <slide-transition v-for="(slide, index) in slides"
                        :key="index">
        <state-wrapper
          v-show="currentStateIndex === index"
          :key="index"
          :name="getSlideStateAlias(index)"
          show>
          <div
            class="slide"
            :style="[slide.imageUrl ? { backgroundImage: `url( '${slide.imageUrl}' )` } : null]">
            <div class="slide__content">
              <h2 class="slide__year">
                {{ slide.year }}
              </h2>

              <h3 class="slide__name">
                {{ slide.name }}
              </h3>

              <div class="slide__description">
                {{ slide.description }}
              </div>
            </div>
          </div>
        </state-wrapper>
      </slide-transition>
    </state-manager>
  </div>
</template>

<script>
import vResize from "vue-resize-directive";
import StateManager from "~/Components/Timeline/Copied/StateManager.vue";
import StateWrapper from "~/Components/Timeline/Copied/StateWrapper.vue";
import SlideTransition from "~/Components/Timeline/SlideTransition.vue";
import vClickHold from "~/Scripts/directives/clickhold";

export default {
  name: "TimeLine",
  components: {
    SlideTransition,
    StateWrapper,
    StateManager,
  },
  directives: {
    clickHold: vClickHold,
    resize: vResize,
  },
  props: {
    slides: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ( {
    currentStateIndex: 0,
    interval: 5000,
    autoPlay: true,
    intervalInstance: null,
    showScrollButtons: false,
  } ),
  computed: {
    currentState () {
      let result = null;

      if ( this.slides && this.slides.length ) {
        result = this.getSlideStateAlias( this.currentStateIndex );
      }

      return result;
    },
    hasSlides () {
      return this.slides?.length > 0;
    },
  },
  watch: {
    autoPlay: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.intervalInstance = window.setInterval( () => {
            this.incrementSlideIndex();
            this.scrollToYearItem();
          }, this.interval );
        } else {
          window.clearInterval( this.intervalInstance );
        }
      },
    },
  },
  methods: {
    /**
     * Turn auto play on or off
     */
    toggleAutoPlay () {
      this.autoPlay = !this.autoPlay;
    },

    /**
     * Create a unique slide alias
     */
    getSlideStateAlias ( index ) {
      return `slide_${index}`;
    },

    /**
     * Move to the next slide
     */
    incrementSlideIndex () {
      if ( this.currentStateIndex < this.slides.length - 1 ) {
        this.currentStateIndex += 1;
      } else {
        this.currentStateIndex = 0;
      }
    },

    /**
     * Move to the previous slide
     */
    decrementSlideIndex () {
      if ( this.currentStateIndex > 0 ) {
        this.currentStateIndex -= 1;
      } else {
        this.currentStateIndex = this.slides.length - 1;
      }
    },

    /**
     * Ensure the year item for the current slide in years nav is scrolled into view
     */
    scrollToYearItem () {
      const { years } = this.$refs;
      const yearItem = document.getElementById( `year-${this.currentStateIndex}` );
      if ( yearItem && years ) {
        try {
          years.scrollTo( yearItem.offsetLeft, 0 );
        } catch {
          years.scrollLeft = yearItem.offsetLeft;
        }
      }
    },

    /**
     * Year list has been resized
     */
    yearsResize () {
      const { years, scrollLeft, scrollRight } = this.$refs;
      const width = years.clientWidth + scrollLeft.clientWidth + scrollRight.clientWidth;

      if ( years.scrollWidth > width ) {
        this.showScrollButtons = true;
      } else {
        this.showScrollButtons = false;
      }
    },

    /**
     * Scroll years nav to the left
     */
    scrollYearsLeft () {
      this.scrollYears( -20 );
    },

    /**
     * Scroll years nav to the right
     */
    scrollYearsRight () {
      this.scrollYears( 20 );
    },

    /**
     * Scroll the years nav to a new position
     *
     * @param {numbver} scrollValue The amount to scroll the years nav
     *                                +ve = scroll right
     *                                -ve = scroll left
     */
    scrollYears ( scrollValue ) {
      const { years } = this.$refs;
      if ( years ) {
        const value = years.scrollLeft + scrollValue;
        try {
          years.scrollTo( value, 0 );
        } catch {
          years.scrollLeft = value;
        }
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.timeline {
  position: relative;
  overflow: hidden;
  background-color: transparent;

  &__button {
    cursor: pointer;
    padding: 0 15px;
    line-height: 100%;
    height: 100%;
    text-align: center;
    text-transform: uppercase;
    border: none;
    border-bottom: medium solid transparent;
    background-color: transparent;
    color: #999;

    &:hover {
      background-color: rgb(239, 236, 230);
    }
  }

  &__header {
    position: absolute;
    top: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    height: 50px;
    background-color: rgba(239, 236, 230, 0.8);
    border-bottom: thin solid #aaaaaa;
    z-index: 1;
  }

  &__toggle {
    min-width: 130px;
  }

  &__prev-next {
    display: none;
    flex: 0 0 auto;
    text-align: right;

    @media (max-width: 991px) {
      display: block;
    }
  }

  &__scroll {
    @media (max-width: 991px) {
      display: none;
    }
  }
}

.nav {
  overflow: hidden;

  &__list {
    position: relative;
    margin: 0;
    padding: 0;
    display: flex;
    align-items: stretch;
    list-style-type: none;
    overflow-y: auto;
    height: 100px;

    @media (max-width: 991px) {
      display: none;
    }
  }

  &__item {
    height: 50px;
  }

  &__active .nav__button {
    color: @color-accent-1;
    border-bottom: medium solid #aaaaaa;
  }
}

.slide {
  display: flex;
  flex-direction: column;
  text-align: left;
  min-height: 500px;
  background-image: linear-gradient(to bottom, white, white 50px, #efece6 0);
  background-position: right;
  background-repeat: no-repeat;
  padding: 80px 30px 30px 30px;
  z-index: 0;

  @media (min-width: 991px) {
    padding: 100px 100px 40px 150px;
  }

  @media (max-width: 991px) {
    // Force the background to be hidden on small screens. If it shows then you cannot read the text
    background-image: linear-gradient(to bottom, white, white 50px, #efece6 0) !important;
  }

  &__year {
    color: @color-accent-1;
    margin-bottom: 2.5rem;
    font-size: 3rem;
  }
}
</style>
